import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../../Components/UI/dialog';
import { Button } from '../../../Components/UI/button';
import { Label } from '../../../Components/UI/label';
import { Textarea } from '../../../Components/UI/textarea';
import { X, ArrowLeft, Check } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { updateCompanyProfile } from './ProfileSectionAPI';
import { toast } from 'sonner';
import { setGlobalLoading } from '../../../Store/Actions';
import { updateAgencyData, userIsAdmin } from '../../../Store/Reducers';

const GoalsSection = ({ open, onClose }) => {
  const { data: agencyData } = useSelector((state) => state.agencyData);
  const isAdmin = useSelector(userIsAdmin);
  const dispatch = useDispatch();

  const [monthlyGoal, setMonthlyGoal] = useState('');
  const [quarterlyGoal, setQuarterlyGoal] = useState('');
  const [yearlyGoal, setYearlyGoal] = useState('');

  const monthlyRef = useRef(null);
  const quarterlyRef = useRef(null);
  const yearlyRef = useRef(null);

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  };

  const resizeAll = () => {
    autoResize(monthlyRef.current);
    autoResize(quarterlyRef.current);
    autoResize(yearlyRef.current);
  };

  useEffect(() => {
    if (!open) return;
    resizeAll();
    // Dialog mount/animation and mobile viewport reflow can settle after this
    // paint, leaving heights measured against a stale width — recheck once
    // the layout has actually finished settling.
    const raf = requestAnimationFrame(resizeAll);
    const timer = setTimeout(resizeAll, 150);
    window.addEventListener('resize', resizeAll);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      window.removeEventListener('resize', resizeAll);
    };
  }, [open, monthlyGoal, quarterlyGoal, yearlyGoal]);

  useEffect(() => {
    if (open && agencyData) {
      const rawGoal = agencyData.Goal || agencyData.CompanyGoal || '';
      let mGoal = '';
      let qGoal = '';
      let yGoal = '';
      try {
        const parsed = JSON.parse(rawGoal);
        if (Array.isArray(parsed) && parsed[0]) {
          const obj = parsed[0];
          mGoal = obj.M || '';
          qGoal = obj.Q || '';
          yGoal = obj.Y || '';
        } else if (parsed && typeof parsed === 'object') {
          mGoal = parsed.M || parsed.monthly || '';
          qGoal = parsed.Q || parsed.quarterly || '';
          yGoal = parsed.Y || parsed.yearly || '';
        } else {
          mGoal = rawGoal;
        }
      } catch (e) {
        mGoal = rawGoal;
      }
      setMonthlyGoal(mGoal);
      setQuarterlyGoal(qGoal);
      setYearlyGoal(yGoal);
    }
  }, [open, agencyData]);

  const handleSubmit = async () => {
    dispatch(setGlobalLoading(true));

    try {
      const goalVal = JSON.stringify([{
        M: monthlyGoal ? monthlyGoal.trim() : '',
        Q: quarterlyGoal ? quarterlyGoal.trim() : '',
        Y: yearlyGoal ? yearlyGoal.trim() : ''
      }]);

      const apiPayload = {
        goal: goalVal,
        pin: 0
      };

      const result = await updateCompanyProfile('GOALCHG', apiPayload);
      if (result.error === false) {
        toast.success('Goals updated.');
        const updatedAgency = {
          ...agencyData,
          Goal: goalVal
        };
        dispatch(updateAgencyData(updatedAgency));
        onClose();
      } else {
        toast.error(result.message || 'Failed to update goals. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to update goals. Please try again.');
      console.error('Error saving goals:', error);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) onClose(); }}>
      <DialogContent className="lg:!max-w-[35vw] max-w-[100vw]">
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center" onClick={onClose}>
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="truncate">Business Goals</div>
              </DialogTitle>
            </div>
          </div>
          <DialogTitle className="hidden sm:block text-lg md:text-xl">
            Business Goals
          </DialogTitle>
          <div className="hidden sm:flex items-center gap-2">
            <DialogClose className="flex-shrink-0" tabIndex={-1} onClick={onClose}>
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-3 sm:px-4 md:px-4 space-y-4 max-h-[100vh]">
          <div className="space-y-2 w-full">
            <div className="flex justify-between items-center">
              <Label htmlFor="monthlyGoal" className="text-sm font-medium">
                Monthly Goal
              </Label>
              <span className="text-xs text-gray-400 font-normal">
                {monthlyGoal.length}/100
              </span>
            </div>
            <Textarea
              id="monthlyGoal"
              ref={monthlyRef}
              placeholder="e.g. Close ₹50L in new business this month."
              value={monthlyGoal}
              onChange={(e) => { setMonthlyGoal(e.target.value); autoResize(e.target); }}
              disabled={!isAdmin}
              maxLength={100}
              rows={2}
              style={{ fieldSizing: 'fixed' }}
              className="w-full text-sm min-h-[48px] resize-none overflow-hidden"
            />
          </div>

          <div className="space-y-2 w-full">
            <div className="flex justify-between items-center">
              <Label htmlFor="quarterlyGoal" className="text-sm font-medium">
                Quarterly Goal
              </Label>
              <span className="text-xs text-gray-400 font-normal">
                {quarterlyGoal.length}/100
              </span>
            </div>
            <Textarea
              id="quarterlyGoal"
              ref={quarterlyRef}
              placeholder="e.g. Onboard 25 new clients this quarter."
              value={quarterlyGoal}
              onChange={(e) => { setQuarterlyGoal(e.target.value); autoResize(e.target); }}
              disabled={!isAdmin}
              maxLength={100}
              rows={2}
              style={{ fieldSizing: 'fixed' }}
              className="w-full text-sm min-h-[48px] resize-none overflow-hidden"
            />
          </div>

          <div className="space-y-2 w-full">
            <div className="flex justify-between items-center">
              <Label htmlFor="yearlyGoal" className="text-sm font-medium">
                Yearly Goal
              </Label>
              <span className="text-xs text-gray-400 font-normal">
                {yearlyGoal.length}/100
              </span>
            </div>
            <Textarea
              id="yearlyGoal"
              ref={yearlyRef}
              placeholder="e.g. Expand operations to 3 new regions."
              value={yearlyGoal}
              onChange={(e) => { setYearlyGoal(e.target.value); autoResize(e.target); }}
              disabled={!isAdmin}
              maxLength={100}
              rows={2}
              style={{ fieldSizing: 'fixed' }}
              className="w-full text-sm min-h-[48px] resize-none overflow-hidden"
            />
          </div>
        </div>

        <div className="flex items-center justify-start gap-3 lg:px-4 px-4 lg:pb-4 pb-4 pt-0">
          <Button
            onClick={handleSubmit}
            className="w-auto"
            disabled={!isAdmin}
          >
            <Check className="h-4 w-4" />
            Save
          </Button>
          <Button
            variant="secondary"
            onClick={onClose}
          >
            <X className="w-3 h-3" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GoalsSection;
